/**
 * Web Vitals wiring (doc 147 §8.7 PERF-06).
 *
 * Subscribes to LCP, INP and CLS and emits structured log lines. Hosting log
 * forwarding aggregates these — a downstream pipe to BigQuery is on the
 * roadmap (F1 operational runbook) when SLO dashboards land.
 *
 * Pure module: `formatMetric` is unit-tested directly, `wireWebVitals` takes
 * the web-vitals lib as an injectable dependency so tests don't need a real
 * browser PerformanceObserver.
 */

export type WebVitalRating = 'good' | 'needs-improvement' | 'poor';

export interface WebVitalMetric {
  name: 'LCP' | 'INP' | 'CLS' | 'FCP' | 'TTFB';
  value: number;
  id: string;
  rating: WebVitalRating;
}

export interface WebVitalsLib {
  onLCP: (cb: (m: WebVitalMetric) => void) => void;
  onINP: (cb: (m: WebVitalMetric) => void) => void;
  onCLS: (cb: (m: WebVitalMetric) => void) => void;
}

/**
 * Build the single structured log line for a metric. CLS is a unitless
 * cumulative-shift score, formatted to 3 decimals; everything else is
 * milliseconds, rounded to the nearest integer.
 */
export function formatMetric(metric: WebVitalMetric): string {
  const value =
    metric.name === 'CLS'
      ? metric.value.toFixed(3)
      : `${Math.round(metric.value)}ms`;
  return `[atlas/web-vitals] ${metric.name}=${value} rating=${metric.rating} id=${metric.id}`;
}

/**
 * Subscribe to the three Core Web Vitals. Emits via `emit` (defaults to
 * `console.info` with the formatted line). The `lib` arg lets tests inject a
 * stub for the web-vitals package.
 */
export function wireWebVitals(
  emit?: (metric: WebVitalMetric) => void,
  lib?: WebVitalsLib,
): void {
  const sink = emit ?? ((metric) => console.info(formatMetric(metric)));
  if (!lib) return; // production wires lib at the call site
  lib.onLCP(sink);
  lib.onINP(sink);
  lib.onCLS(sink);
}
